import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { GOOGLE_FONTS, buildGoogleFontsUrl, GoogleFont, FEATURED_FONTS, FONT_STATS } from '../../font-explorer/fonts'
import { pairingsApi, PairingCategoryWithNumericId } from '../../font-explorer/api/pairingsApi'

const DEFAULT_SAMPLE_TEXT = 'The quick brown fox jumps over the lazy dog'
const FONTS_PER_PAGE = 100

type SortOption = 'featured' | 'alphabetical'
type FontCategory = 'sans-serif' | 'serif' | 'display' | 'handwriting' | 'monospace'
type FontRole = 'display' | 'body'

const FONT_CATEGORIES: FontCategory[] = ['sans-serif', 'serif', 'display', 'handwriting', 'monospace']

export default function CreatePairingPage() {
  const navigate = useNavigate()

  // Selection state
  const [displayFont, setDisplayFont] = useState('')
  const [bodyFont, setBodyFont] = useState('')
  const [name, setName] = useState('')
  const [categoryId, setCategoryId] = useState<number | ''>('')
  const [categories, setCategories] = useState<PairingCategoryWithNumericId[]>([])

  // Browser state
  const [sampleText, setSampleText] = useState(DEFAULT_SAMPLE_TEXT)
  const [selectedCategories, setSelectedCategories] = useState<Set<FontCategory>>(new Set(FONT_CATEGORIES))
  const [searchQuery, setSearchQuery] = useState('')
  const [fontSize, setFontSize] = useState(32)
  const [loadedFonts, setLoadedFonts] = useState<Set<string>>(new Set())
  const [visibleCount, setVisibleCount] = useState(FONTS_PER_PAGE)
  const [sortBy, setSortBy] = useState<SortOption>('featured')

  // Submit state
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  // Fetch categories on mount
  useEffect(() => {
    pairingsApi.listCategoriesWithIds().then(setCategories).catch(() => {})
  }, [])

  // Category toggles
  const toggleCategory = (cat: FontCategory) => {
    setSelectedCategories(prev => {
      const next = new Set(prev)
      if (next.has(cat)) next.delete(cat)
      else next.add(cat)
      return next
    })
  }

  // Filter and sort
  const filteredFonts = useMemo(() => {
    let fonts = GOOGLE_FONTS.filter(font => {
      const matchesCategory = selectedCategories.has(font.category as FontCategory)
      const matchesSearch = font.family.toLowerCase().includes(searchQuery.toLowerCase())
      return matchesCategory && matchesSearch
    })

    if (sortBy === 'featured' && !searchQuery) {
      const featuredSet = new Set(FEATURED_FONTS)
      fonts = [
        ...fonts.filter(f => featuredSet.has(f.family)),
        ...fonts.filter(f => !featuredSet.has(f.family)),
      ]
    } else if (sortBy === 'alphabetical') {
      fonts = [...fonts].sort((a, b) => a.family.localeCompare(b.family))
    }

    return fonts
  }, [selectedCategories, searchQuery, sortBy])

  const visibleFonts = useMemo(() => filteredFonts.slice(0, visibleCount), [filteredFonts, visibleCount])

  // Load fonts when visible
  useEffect(() => {
    const fontsToLoad = visibleFonts.filter(f => !loadedFonts.has(f.family)).map(f => f.family)
    if (fontsToLoad.length > 0) {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = buildGoogleFontsUrl(fontsToLoad)
      document.head.appendChild(link)
      setLoadedFonts(prev => {
        const next = new Set(prev)
        fontsToLoad.forEach(f => next.add(f))
        return next
      })
    }
  }, [visibleFonts, loadedFonts])

  // Load selected fonts if not already loaded
  useEffect(() => {
    const fontsToLoad = [displayFont, bodyFont].filter(f => f && !loadedFonts.has(f))
    if (fontsToLoad.length > 0) {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = buildGoogleFontsUrl(fontsToLoad)
      document.head.appendChild(link)
      setLoadedFonts(prev => {
        const next = new Set(prev)
        fontsToLoad.forEach(f => next.add(f))
        return next
      })
    }
  }, [displayFont, bodyFont, loadedFonts])

  // Reset visible on filter change
  useEffect(() => {
    setVisibleCount(FONTS_PER_PAGE)
  }, [selectedCategories, searchQuery, sortBy])

  const loadMore = () => {
    setVisibleCount(prev => Math.min(prev + FONTS_PER_PAGE, filteredFonts.length))
  }

  const handleSelect = (family: string, role: FontRole) => {
    if (role === 'display') {
      setDisplayFont(prev => prev === family ? '' : family)
    } else {
      setBodyFont(prev => prev === family ? '' : family)
    }
  }

  const handleSubmit = async () => {
    if (!displayFont || !bodyFont || !categoryId) {
      setError('Display font, body font, and category are required.')
      return
    }

    setSubmitting(true)
    setError('')

    try {
      await pairingsApi.createPairing({
        display_font: displayFont,
        body_font: bodyFont,
        name: name || undefined,
        font_pairing_category_id: categoryId as number,
      })
      window.location.href = '/font-pairings'
    } catch (err: any) {
      if (err.message === 'sign_in_required') {
        setError('sign_in_required')
      } else {
        setError(err.message || 'Failed to create pairing.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const bothSelected = !!displayFont && !!bodyFont

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky selection bar */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          {/* Compact bar when both selected, expanded otherwise */}
          <div className="flex items-center gap-4 flex-wrap">
            {/* Back button */}
            <a
              href="/font-pairings"
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </a>

            <h1 className="text-sm font-semibold text-gray-900 flex-shrink-0">New Pairing</h1>

            {/* Selection pills */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <SelectionPill
                label="Display"
                font={displayFont}
                isLoaded={loadedFonts.has(displayFont)}
                onClear={() => setDisplayFont('')}
              />
              <span className="text-gray-300 text-xs">+</span>
              <SelectionPill
                label="Body"
                font={bodyFont}
                isLoaded={loadedFonts.has(bodyFont)}
                onClear={() => setBodyFont('')}
              />
            </div>

            {/* Name + Category + Submit */}
            {bothSelected && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name (optional)"
                  className="w-36 px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
                <select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : '')}
                  className="px-2 py-1.5 text-xs border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent cursor-pointer"
                >
                  <option value="">Category...</option>
                  {categories.map((cat) => (
                    <option key={cat.numericId} value={cat.numericId}>
                      {cat.emoji} {cat.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !categoryId}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  {submitting ? 'Creating...' : 'Create Pairing'}
                </button>
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="mt-2">
              {error === 'sign_in_required' ? (
                <div className="text-xs bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  <span className="font-medium text-amber-800">Sign in required</span>
                  {' — '}
                  <a href="/users/sign-in" className="underline text-amber-700 hover:text-amber-900">
                    Sign in
                  </a>
                  {' '}as an admin to create font pairings.
                </div>
              ) : (
                <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Preview bar when both fonts selected */}
          {bothSelected && (
            <div className="mt-3 flex gap-6 py-3 px-4 bg-gray-50 rounded-lg border border-gray-100">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Display</p>
                <p
                  className="text-xl text-gray-900 truncate transition-opacity duration-300"
                  style={{
                    fontFamily: loadedFonts.has(displayFont) ? `'${displayFont}', sans-serif` : 'sans-serif',
                    opacity: loadedFonts.has(displayFont) ? 1 : 0.4,
                  }}
                >
                  {sampleText}
                </p>
              </div>
              <div className="w-px bg-gray-200" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Body</p>
                <p
                  className="text-sm text-gray-700 truncate transition-opacity duration-300"
                  style={{
                    fontFamily: loadedFonts.has(bodyFont) ? `'${bodyFont}', sans-serif` : 'sans-serif',
                    opacity: loadedFonts.has(bodyFont) ? 1 : 0.4,
                  }}
                >
                  {sampleText}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Font browser controls */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search fonts..."
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-600">Categories</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedCategories(new Set(FONT_CATEGORIES))}
                    className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer"
                  >
                    All
                  </button>
                  <span className="text-xs text-gray-300">|</span>
                  <button
                    onClick={() => setSelectedCategories(new Set())}
                    className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer"
                  >
                    None
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {FONT_CATEGORIES.map(cat => (
                  <label
                    key={cat}
                    className={`
                      flex items-center gap-1.5 px-2.5 py-1.5 text-xs border rounded-lg cursor-pointer transition-all
                      ${selectedCategories.has(cat)
                        ? 'bg-gray-900 text-white border-gray-900'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                      }
                    `}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCategories.has(cat)}
                      onChange={() => toggleCategory(cat)}
                      className="sr-only"
                    />
                    <span className="capitalize">{cat}</span>
                    <span className={`${selectedCategories.has(cat) ? 'text-gray-300' : 'text-gray-400'}`}>
                      {FONT_STATS.byCategory[cat]}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Sort */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Sort</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent cursor-pointer"
              >
                <option value="featured">Featured First</option>
                <option value="alphabetical">Alphabetical</option>
              </select>
            </div>

            {/* Font Size */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Size: {fontSize}px</label>
              <input
                type="range"
                min="16"
                max="96"
                value={fontSize}
                onChange={(e) => setFontSize(Number(e.target.value))}
                className="w-32 cursor-pointer"
              />
            </div>
          </div>

          {/* Sample text */}
          <div className="mt-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">Sample Text</label>
            <input
              type="text"
              value={sampleText}
              onChange={(e) => setSampleText(e.target.value)}
              placeholder="Type to preview..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Font grid */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <p className="text-xs text-gray-500 mb-4">
          {filteredFonts.length.toLocaleString()} fonts — click a button to assign as Display or Body font
        </p>

        <div className="grid gap-4">
          {visibleFonts.map((font) => (
            <FontCard
              key={font.family}
              font={font}
              sampleText={sampleText || DEFAULT_SAMPLE_TEXT}
              fontSize={fontSize}
              isLoaded={loadedFonts.has(font.family)}
              isFeatured={FEATURED_FONTS.includes(font.family)}
              isDisplay={displayFont === font.family}
              isBody={bodyFont === font.family}
              onSelect={handleSelect}
            />
          ))}
        </div>

        {/* Load more */}
        {visibleCount < filteredFonts.length && (
          <div className="mt-8 text-center">
            <button
              onClick={loadMore}
              className="px-6 py-3 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer"
            >
              Load More ({(filteredFonts.length - visibleCount).toLocaleString()} remaining)
            </button>
          </div>
        )}

        {filteredFonts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No fonts found matching your criteria.</p>
          </div>
        )}
      </main>
    </div>
  )
}

/* Selection pill in the sticky bar */
function SelectionPill({
  label,
  font,
  isLoaded,
  onClear,
}: {
  label: string
  font: string
  isLoaded: boolean
  onClear: () => void
}) {
  if (!font) {
    return (
      <span className="px-3 py-1.5 text-xs border border-dashed border-gray-300 rounded-md text-gray-400 bg-gray-50">
        {label}: none
      </span>
    )
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-900 rounded-md bg-gray-900 text-white">
      <span className="font-medium">{label}:</span>
      <span
        style={{
          fontFamily: isLoaded ? `'${font}', sans-serif` : 'sans-serif',
        }}
      >
        {font}
      </span>
      <button
        onClick={onClear}
        className="ml-1 hover:text-gray-300 transition-colors cursor-pointer"
        title={`Clear ${label} font`}
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </span>
  )
}

/* Font card with action buttons */
interface FontCardProps {
  font: GoogleFont
  sampleText: string
  fontSize: number
  isLoaded: boolean
  isFeatured: boolean
  isDisplay: boolean
  isBody: boolean
  onSelect: (family: string, role: FontRole) => void
}

const categoryColors: Record<string, string> = {
  'sans-serif': 'bg-blue-100 text-blue-700',
  'serif': 'bg-amber-100 text-amber-700',
  'display': 'bg-purple-100 text-purple-700',
  'handwriting': 'bg-pink-100 text-pink-700',
  'monospace': 'bg-green-100 text-green-700',
}

function FontCard({ font, sampleText, fontSize, isLoaded, isFeatured, isDisplay, isBody, onSelect }: FontCardProps) {
  const isSelected = isDisplay || isBody

  return (
    <div
      className={`bg-white rounded-xl border p-5 hover:shadow-md transition-all ${
        isSelected ? 'border-gray-900 ring-1 ring-gray-900' : isFeatured ? 'border-gray-300' : 'border-gray-200'
      }`}
    >
      <div className="flex flex-wrap items-start gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900">{font.family}</h3>
          <span className={`px-2 py-0.5 text-xs rounded-full ${categoryColors[font.category]}`}>
            {font.category}
          </span>
          {isFeatured && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-gray-900 text-white">Popular</span>
          )}
          {isDisplay && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-indigo-100 text-indigo-700 font-medium">
              Display
            </span>
          )}
          {isBody && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-teal-100 text-teal-700 font-medium">
              Body
            </span>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-1.5 flex-shrink-0">
          <button
            onClick={() => onSelect(font.family, 'display')}
            className={`px-2.5 py-1 text-xs rounded-full transition-all cursor-pointer ${
              isDisplay
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {isDisplay ? 'Display ✓' : 'Use as Display'}
          </button>
          <button
            onClick={() => onSelect(font.family, 'body')}
            className={`px-2.5 py-1 text-xs rounded-full transition-all cursor-pointer ${
              isBody
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {isBody ? 'Body ✓' : 'Use as Body'}
          </button>
        </div>
      </div>

      <div
        className="text-gray-800 leading-relaxed transition-opacity duration-300 break-words"
        style={{
          fontFamily: isLoaded ? `'${font.family}', ${font.category === 'monospace' ? 'monospace' : 'sans-serif'}` : 'sans-serif',
          fontSize: `${fontSize}px`,
          opacity: isLoaded ? 1 : 0.5,
        }}
      >
        {sampleText}
      </div>

      <div className="mt-3 flex items-center gap-4 text-xs text-gray-400">
        <span>Weights: {font.variants.join(', ')}</span>
        {font.subsets.length > 1 && (
          <span>
            Subsets: {font.subsets.slice(0, 5).join(', ')}
            {font.subsets.length > 5 ? ` +${font.subsets.length - 5}` : ''}
          </span>
        )}
      </div>
    </div>
  )
}
